import { useCallback, useState } from 'react';
import { signIn, confirmSignIn } from 'aws-amplify/auth';
import { mapAuthError } from '../utils/mapAuthError';

export type AuthStep =
  | 'SIGN_IN'
  | 'CONFIRM_EMAIL_CODE'
  | 'NEW_PASSWORD_REQUIRED'
  | 'DONE';

export interface UseAuthFlowResult {
  step: AuthStep;
  loading: boolean;
  error: string | null;
  username: string;
  codeDeliveryDestination: string | null;
  signIn: (username: string, password: string) => Promise<void>;
  confirmCode: (code: string) => Promise<void>;
  confirmNewPassword: (newPassword: string) => Promise<void>;
}

type AmplifySignInStep = string;

function mapNextStep(step: AmplifySignInStep): AuthStep {
  switch (step) {
    case 'DONE':
      return 'DONE';
    case 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE':
      return 'CONFIRM_EMAIL_CODE';
    case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
      return 'NEW_PASSWORD_REQUIRED';
    default:
      return 'SIGN_IN';
  }
}

export function useAuthFlow(onSuccess: () => void): UseAuthFlowResult {
  const [step, setStep] = useState<AuthStep>('SIGN_IN');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [username, setUsername] = useState('');
  const [codeDeliveryDestination, setCodeDeliveryDestination] = useState<string | null>(null);

  const applyNextStep = useCallback((nextStep: { signInStep: string; codeDeliveryDetails?: { destination?: string } }) => {
    const mapped = mapNextStep(nextStep.signInStep);
    setStep(mapped);
    if (nextStep.codeDeliveryDetails?.destination) {
      setCodeDeliveryDestination(nextStep.codeDeliveryDetails.destination);
    }
    if (mapped === 'DONE') {
      onSuccess();
    }
  }, [onSuccess]);

  const doSignIn = useCallback(async (u: string, p: string) => {
    setLoading(true);
    setError(null);
    setUsername(u);
    try {
      const result = await signIn({ username: u, password: p });
      applyNextStep(result.nextStep as { signInStep: string });
    } catch (e) {
      setError(mapAuthError(e));
    } finally {
      setLoading(false);
    }
  }, [applyNextStep]);

  const confirmCode = useCallback(async (code: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await confirmSignIn({ challengeResponse: code });
      applyNextStep(result.nextStep as { signInStep: string });
    } catch (e) {
      setError(mapAuthError(e));
    } finally {
      setLoading(false);
    }
  }, [applyNextStep]);

  const confirmNewPassword = useCallback(async (newPassword: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await confirmSignIn({ challengeResponse: newPassword });
      applyNextStep(result.nextStep as { signInStep: string });
    } catch (e) {
      setError(mapAuthError(e));
    } finally {
      setLoading(false);
    }
  }, [applyNextStep]);

  return {
    step,
    loading,
    error,
    username,
    codeDeliveryDestination,
    signIn: doSignIn,
    confirmCode,
    confirmNewPassword,
  };
}
